import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';

interface RgbColor {
    r: number;
    g: number;
    b: number;
}

@Injectable({
    providedIn: 'root'
})
export class TenantThemeService {
    private readonly defaultFavicon = '/shared/assets/icons/favicon.png';

    constructor(
        @Inject(DOCUMENT) private readonly document: Document,
        @Inject(PLATFORM_ID) private readonly platformId: object
    ) { }

    public applyTheme(tenant: any): void {
        if (!isPlatformBrowser(this.platformId)) return;

        const root = this.document.documentElement;

        root.setAttribute('data-whitelabel', '');
        this.applyFonts(root, tenant);
        this.applyFavicon(tenant?.logo);

        Object.entries(tenant?.colors ?? {}).forEach(([colorName, color]) => {
            this.applyColor(
                root,
                colorName,
                color
            );
        });

        this.applyCustomVariables(root, tenant);
    }

    public resetTheme(): void {
        if (!isPlatformBrowser(this.platformId)) return;

        const root = this.document.documentElement;

        root.removeAttribute('style');
        root.removeAttribute('data-whitelabel');
    }

    private applyFavicon(logo?: unknown): void {
        const faviconLinks = Array.from(
            this.document.head.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]')
        );
        const favicon = faviconLinks[0] ?? this.document.createElement('link');

        if (!faviconLinks.length) {
            favicon.rel = 'icon';
            this.document.head.appendChild(favicon);
        }

        favicon.type = 'image/png';
        favicon.href = this.isValidFavicon(logo) ? logo.trim() : this.defaultFavicon;

        faviconLinks.slice(1).forEach((link) => link.remove());
    }

    private isValidFavicon(logo: unknown): logo is string {
        if (typeof logo !== 'string' || !logo.trim()) {
            return false;
        }

        try {
            const url = new URL(logo.trim(), this.document.baseURI);
            return ['http:', 'https:', 'data:', 'blob:'].includes(url.protocol);
        } catch {
            return false;
        }
    }

    private applyFonts(root: HTMLElement, tenant: any): void {
        if (tenant?.primaryFont) {
            root.style.setProperty(
                '--ion-font-family',
                this.createFontFamily(tenant.primaryFont)
            );
        }

        if (tenant?.secondaryFont) {
            root.style.setProperty(
                '--font-family-secondary',
                this.createFontFamily(tenant.secondaryFont)
            );
        }
    }

    private applyColor(
        root: HTMLElement,
        colorName: any,
        color: any
    ): void {
        if (!color?.default) return;

        const parsedDefault = this.parseColor(color.default);

        const rgb =
            color.rgb ??
            (parsedDefault
                ? `${parsedDefault.r}, ${parsedDefault.g}, ${parsedDefault.b}`
                : undefined);

        const contrast =
            color.contrast ??
            (parsedDefault
                ? this.calculateContrast(parsedDefault)
                : undefined);

        const parsedContrast = contrast
            ? this.parseColor(contrast)
            : undefined;

        const contrastRgb =
            color.contrastRgb ??
            (parsedContrast
                ? `${parsedContrast.r}, ${parsedContrast.g}, ${parsedContrast.b}`
                : undefined);

        const shade =
            color.shade ??
            (parsedDefault
                ? this.createShade(parsedDefault)
                : undefined);

        const tint =
            color.tint ??
            (parsedDefault
                ? this.createTint(parsedDefault)
                : undefined);

        this.setVariable(
            root,
            `--ion-color-${colorName}`,
            color.default
        );

        this.setVariable(
            root,
            `--ion-color-${colorName}-rgb`,
            rgb
        );

        this.setVariable(
            root,
            `--ion-color-${colorName}-contrast`,
            contrast
        );

        this.setVariable(
            root,
            `--ion-color-${colorName}-contrast-rgb`,
            contrastRgb
        );

        this.setVariable(
            root,
            `--ion-color-${colorName}-shade`,
            shade
        );

        if (colorName !== 'primary') {
            this.setVariable(
                root,
                `--ion-color-${colorName}-tint`,
                tint
            );
        }
    }

    private applyCustomVariables(
        root: HTMLElement,
        tenant: any
    ): void {
        const primary = tenant?.colors?.primary?.default;
        const secondary = tenant?.colors?.secondary?.default;

        if (primary && secondary) {
            root.style.setProperty(
                '--app-primary-gradient',
                `linear-gradient(90deg, ${primary} 0%, ${secondary} 100%)`
            );
        }
    }

    private setVariable(
        root: HTMLElement,
        name: string,
        value?: string
    ): void {
        if (!value) return;

        root.style.setProperty(name, value);
    }

    private createFontFamily(fontName: string): string {
        const sanitizedFont = fontName.replace(/["']/g, '').trim();

        return `"${sanitizedFont}", sans-serif`;
    }

    private parseColor(color: string): RgbColor | undefined {
        const normalizedColor = color.trim();

        if (normalizedColor.startsWith('#')) {
            return this.parseHexColor(normalizedColor);
        }

        if (normalizedColor.startsWith('rgb')) {
            return this.parseRgbColor(normalizedColor);
        }

        return undefined;
    }

    private parseHexColor(color: string): RgbColor | undefined {
        const hex = color.replace('#', '');

        if (hex.length === 3) {
            return {
                r: parseInt(hex[0] + hex[0], 16),
                g: parseInt(hex[1] + hex[1], 16),
                b: parseInt(hex[2] + hex[2], 16)
            };
        }

        if (hex.length === 6 || hex.length === 8) {
            return {
                r: parseInt(hex.substring(0, 2), 16),
                g: parseInt(hex.substring(2, 4), 16),
                b: parseInt(hex.substring(4, 6), 16)
            };
        }

        return undefined;
    }

    private parseRgbColor(color: string): RgbColor | undefined {
        const match = color.match(
            /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/
        );

        if (!match) return undefined;

        return {
            r: Number(match[1]),
            g: Number(match[2]),
            b: Number(match[3])
        };
    }

    private createShade(color: RgbColor): string {
        return this.toHex({
            r: Math.round(color.r * 0.88),
            g: Math.round(color.g * 0.88),
            b: Math.round(color.b * 0.88)
        });
    }

    private createTint(color: RgbColor): string {
        return this.toHex({
            r: Math.round(color.r + (255 - color.r) * 0.1),
            g: Math.round(color.g + (255 - color.g) * 0.1),
            b: Math.round(color.b + (255 - color.b) * 0.1)
        });
    }

    private calculateContrast(color: RgbColor): string {
        const luminance =
            (0.299 * color.r +
                0.587 * color.g +
                0.114 * color.b) /
            255;

        return luminance > 0.55 ? '#000000' : '#FFFFFF';
    }

    private toHex(color: RgbColor): string {
        const toChannel = (value: number) =>
            Math.max(0, Math.min(255, value))
                .toString(16)
                .padStart(2, '0');

        return `#${toChannel(color.r)}${toChannel(color.g)}${toChannel(color.b)}`;
    }
}
